import {
  Button,
  Alert,
  Checkbox,
  Tooltip,
  TextInput,
  Combobox as Combo,
  useCombobox,
  CloseButton,
  ActionIcon,
  Popover,
  Loader,
  ComboboxProps,
  CheckIcon
} from '@mantine/core'
import {
  HiSearch,
  HiPlusCircle,
  HiOutlineExternalLink,
  HiX,
  HiPlus,
  HiTrash,
  HiPencil,
  HiCheckCircle,
  HiSelector
} from 'react-icons/hi'
import {AutoQ as A} from 'api/autoQ'
import {useDebounceMutation} from 'components/hooks/useDebounceMutation'
import {usePortal} from '../portal/portal'
import {SAvatar} from '../portal/SAvatar'
import {Comparison, Fmt, QuestionCfgCommon} from 'shared/forecast'
import {entries, isEmpty, mapToObj, pipe, sortBy} from 'remeda'
import {
  ComparisonCfgKey,
  comparisonCfgKey,
  ComparisonType,
  comparisonTypeMd,
  zComparison
} from 'shared/forecast/comparison'
import {LabelEmptyList, LabelForm, LabelType, Markdown, WrapIf} from 'shared/ui'
import {dbFormSubset, DbFormSubset, dbFormSubsetMantine} from 'components/hooks/useDbForm'
import {InfoLabel} from 'components/Info'
import {match} from 'ts-pattern'
import {nanoid} from 'nanoid'
import {motion} from 'motion/react'
import {ComparisonLink} from 'shared/forecast/results'
import {notNullOrEmpty, isNullOrEmpty, mapEntries} from 'shared/collection'
import {ComparisonTypeBadge} from 'shared/forecast/results'
import {UIState, useUIItem} from 'components/hooks/useUIState'
import {z} from 'zod'
import {useContext, useState} from 'react'
import {QuestionContext} from './questionComponentTypes'
import {useForm, zodResolver} from '@mantine/form'
import {RichText} from 'components/form/RichText'
import {SColorInput} from 'components/form/SColorInput'
import {StringNumberInput} from 'components/form/StringNumberInput'
import {twMerge} from 'tailwind-merge'
import {useQueryString} from 'utils/useQueryString'
import {okOrThrow} from 'shared/promise'
import {useQuestionUIItem} from './useQuestions'

export type ComparisonSearchProps = A.GetComparisonsRequest['question']

type ComparisonWithId = Comparison & {id: string}

export type ComparisonQForm = Pick<
  DbFormSubset<Pick<QuestionCfgCommon, 'comparisons' | 'forecastType' | 'amountType' | 'unitLabel'>>,
  'values' | 'setValues' | 'getInputProps' | 'isDirty'
>

const zComparisonSearch = z.record(zComparison).default({}).describe('comparison-search')

const zComparisonUserEdit = pipe(zComparison.shape, ({title, color, description, url, value}) =>
  z.object({
    title: title.default(''),
    color,
    description: description.default(''),
    url: url.default(''),
    value: value.optional()
  })
)
const zComparisonUserEditValidate = zComparisonUserEdit.extend({
  title: z.string().min(1, 'Title is required'),
  value: z.number({message: 'A numeric value is required'})
})
export type ComparisonEdit = z.infer<typeof zComparisonUserEdit>

const flatComparisons = (comparisons: Record<string, Comparison>) =>
  entries(comparisons)
    .filter(([_, c]) => c != null)
    .map(([id, c]) => ({...c!, id}) as ComparisonWithId)

export type UseComparisonProps = {qForm: ComparisonQForm; comparisonKey: ComparisonCfgKey}
export const useComparisonNew = ({qForm, comparisonKey}: UseComparisonProps) => {
  const {
    workspace_id,
    profile,
    workspace: {features}
  } = usePortal()
  const searchEnabled = features['comparison-search'] == true
  const q = qForm.values!
  const key = comparisonCfgKey(comparisonKey)
  const comparisonsForm = dbFormSubset<Record<string, Comparison>>(qForm, `comparisons.${key}`)
  const questionCtx = useContext(QuestionContext)
  const subPath = [questionCtx?.question_id, questionCtx?.subquestion_id, key].filter(notNullOrEmpty)
  const searchState = useQuestionUIItem({
    subPath,
    schema: zComparisonSearch,
    initial: {}
  })
  const comparisons = flatComparisons(comparisonsForm.values ?? {})
  const searchComparisons = searchState.values ? flatComparisons(searchState.values) : null
  const allComparisons = [...comparisons, ...(searchComparisons ?? [])]

  const newForm = useForm<ComparisonEdit>({
    initialValues: {title: '', description: '', url: ''} as ComparisonEdit,
    validate: zodResolver(zComparisonUserEditValidate)
  })

  const searchMute = useDebounceMutation({
    mutationFn: (vars: {}, signal: AbortSignal) =>
      A.getComparisons(
        {
          options: {workspace_id, profile_id: profile.id},
          question: q,
          existingComparisons: allComparisons
        },
        {signal}
      ).then(okOrThrow),
    onSuccess: data => {
      searchState.setValues(mapToObj(data.comparisons ?? [], c => [nanoid(6), c]))
    }
  })
  const toggleResult = ({id, ...comparison}: ComparisonWithId) => {
    if ((comparisonsForm.values ?? {})[id]) {
      comparisonsForm.setFieldValue(id, null)
      if (searchState.values?.[id] == null) searchState.setValues({[id]: comparison}) // if it's not in the search results, add it because to the user it's a checkbox bot a bin
    } else {
      comparisonsForm.setFieldValue(id, zComparison.strip().parse(comparison))
    }
  }
  const isSelected = (comparison: ComparisonWithId) =>
    comparisons.some(c => c.value == comparison.value && c?.title == comparison.title)
  const fmt = (v: number) => Fmt.fmt(v, Fmt.getOptions({...q!, unitLabel: ''} as any).absolute.default)

  const clearUnselected = () => searchState.del()

  const addComparison = (v: ComparisonEdit) => {
    if (newForm.validate().hasErrors) return
    const id = nanoid()
    comparisonsForm.setFieldValue(id, zComparison.strip().parse({...v, by: profile.id}))
    newForm.reset()
  }

  const {query, setQuery} = useQueryString(z.object({editing: z.array(z.string()).optional()}))
  const isEditing = (id: string) => query?.editing?.includes(id)
  const setEditing = (id: string, v: boolean) =>
    setQuery({editing: v ? [...(query?.editing ?? []), id] : query?.editing?.filter(k => k != id)})

  return {
    searchMute,
    comparisons,
    question: q,
    toggleResult,
    isSelected,
    fmt,
    searchComparisons,
    searchEnabled,
    clearUnselected,
    newForm,
    addComparison,
    isEditing,
    setEditing,
    comparisonsForm
  }
}

export type UseComparison = ReturnType<typeof useComparisonNew>
type ComparisonSectionPropsExtra = {
  mode?: 'new' | 'existing'
  className?: string
}

export const ComparisonSection = ({uProps, ...props}: {uProps: UseComparisonProps} & ComparisonSectionPropsExtra) => (
  <ComparisonSectionControlled uComparison={useComparisonNew(uProps)} {...props} />
)

export const ComparisonSectionControlled = ({
  uComparison: {
    comparisons: cs,
    comparisonsForm,
    question: q,
    searchMute: sm,
    toggleResult,
    isSelected,
    fmt,
    searchComparisons,
    searchEnabled,
    clearUnselected,
    newForm,
    addComparison,
    isEditing,
    setEditing
  },
  mode = 'existing',
  className
}: {
  uComparison: UseComparison
  mode?: 'new' | 'existing'
  className?: string
}) => {
  const [newFormOpen, setNewFormOpen] = useState(false)

  return (
    <div className={twMerge('mt-4 space-y-4', className)}>
      <div className='space-y-2'>
        {match(sm)
          .with({isError: true}, ({error}) => <Alert color='red'>{error.message}</Alert>)
          .otherwise(() => {
            const selectedComparisons = cs.map((c: ComparisonWithId) => ({...c, selected: true}))
            const unselectedComparisons = (searchComparisons ?? [])
              .filter((c: ComparisonWithId) => !isSelected(c))
              .map((c: ComparisonWithId) => ({...c, selected: false}))
            const allComparisons = sortBy(
              [...selectedComparisons, ...unselectedComparisons],
              [c => c.selected, 'desc'],
              c => c.value
            )
            return (
              <div className='flex gap-2 flex-col'>
                <div className='flex justify-between items-center'>
                  <div className='flex gap-2 w-full'>
                    <div>
                      <LabelType className='text-sm'>Comparisons</LabelType>
                    </div>
                    <div className='ml-auto'>
                      <InfoLabel className='text-xs'>
                        Base rates or forecasts for this question displayed in the results
                      </InfoLabel>
                    </div>
                  </div>
                </div>
                {isEmpty(allComparisons) && mode == 'existing' && (
                  <LabelEmptyList className='mb-[-5px] text-sm'>no comparisons yet...</LabelEmptyList>
                )}
                {notNullOrEmpty(allComparisons) && (
                  <ul className='space-y-2'>
                    {allComparisons.map(c => (
                      <motion.li
                        key={c.id}
                        className={`flex gap-2 group items-center`}
                        layout='position'
                        transition={{duration: 0.1}}>
                        <Checkbox checked={c.selected} onChange={() => toggleResult(c)} />
                        {isEditing(c.id) ?
                          <div className='w-full my-4 flex gap-2 border rounded-md p-2'>
                            <ComparisonInputs
                              className='grow'
                              form={dbFormSubset(comparisonsForm, c.id)}
                              {...{fmt, unitLabel: q.unitLabel}}
                            />
                            <ActionIcon className='self-start' onClick={() => setEditing(c.id, false)}>
                              <HiCheckCircle />
                            </ActionIcon>
                          </div>
                        : <>
                            <ComparisonRowReadonly comparison={c} fmt={fmt} unitLabel={q.unitLabel} />
                            <div className='ml-auto min-w-[2em]'>
                              {c.selected && (
                                <ActionIcon
                                  onClick={() => setEditing(c.id, true)}
                                  className={'opacity-40 group-hover:opacity-100'}>
                                  <HiPencil />
                                </ActionIcon>
                              )}
                            </div>
                          </>
                        }
                      </motion.li>
                    ))}
                  </ul>
                )}
                {/* variant=subtle buttons look nicer when the text is aligned rather than the button edges */}
                <div
                  className={twMerge(
                    'flex gap-2 mb-[-8px] mr-[-8px]',
                    notNullOrEmpty(allComparisons) && 'justify-end'
                  )}>
                  {notNullOrEmpty(unselectedComparisons) && (
                    <Button
                      size='sm'
                      variant='subtle'
                      color='gray'
                      className=''
                      onClick={() => {
                        clearUnselected()
                      }}
                      leftSection={<HiTrash />}>
                      <div>
                        Clear <b>{unselectedComparisons?.length}</b> un-selected
                      </div>
                    </Button>
                  )}

                  {mode == 'existing' && (
                    <Popover opened={newFormOpen} onDismiss={() => setNewFormOpen(false)}>
                      <Popover.Target>
                        <Button
                          className='ml-auto'
                          variant='subtle'
                          size='sm'
                          leftSection={<HiPlus />}
                          onClick={() => {
                            setNewFormOpen(true)
                          }}>
                          New Comparison
                        </Button>
                      </Popover.Target>
                      <Popover.Dropdown>
                        <div className='flex flex-col max-w-[50em]'>
                          <div className='absolute top-2 right-2 '>
                            <CloseButton
                              onClick={() => {
                                newForm.reset()
                                setNewFormOpen(false)
                              }}
                            />
                          </div>
                          <ComparisonInputs
                            className='grow'
                            form={dbFormSubsetMantine(newForm)}
                            {...{fmt, unitLabel: q.unitLabel}}
                          />
                          <Tooltip
                            label={
                              <div>
                                Add comparison
                                {entries(newForm.errors).map(([k, e]) => (
                                  <div>
                                    {k}: {e}
                                  </div>
                                ))}
                              </div>
                            }
                            position='left'>
                            <div>
                              <Button
                                disabled={isNullOrEmpty(newForm.values.title) || notNullOrEmpty(newForm.errors)}
                                className='self-end min-w-[6em] mt-4'
                                type='submit'
                                variant='filled'
                                leftSection={<HiPlusCircle />}
                                onClick={e => {
                                  e.preventDefault()
                                  if (newForm.validate().hasErrors) return
                                  addComparison(newForm.values)
                                  setNewFormOpen(false)
                                }}>
                                Add Comparison
                              </Button>
                            </div>
                          </Tooltip>
                        </div>
                      </Popover.Dropdown>
                    </Popover>
                  )}

                  {searchEnabled && (
                    <Button
                      size='sm'
                      disabled={sm.isPending}
                      leftSection={sm.isPending ? <Loader size='xs' /> : <HiSearch />}
                      onClick={() => sm.mutate({})}>
                      {isEmpty(allComparisons) ? 'Search the web for comparisons' : 'Search for more'}
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
      </div>
    </div>
  )
}

const ComparisonRowReadonly = ({
  comparison: {title, description, color, value, by, url, type},
  fmt,
  unitLabel,
  profilesUI,
  longLabel
}: {
  comparison: Comparison
  fmt: (n: number) => string
  unitLabel?: string
  profilesUI?: Record<string, any>
  longLabel?: boolean
}) => (
  <>
    <div className='flex gap-2 grow pr-2 items-center'>
      {profilesUI && <SAvatar profile={profilesUI[by]} size='sm' />}
      {type && <ComparisonTypeBadge type={type} longLabel={longLabel} />}
      <WrapIf
        if={notNullOrEmpty(description)}
        wrap={c => (
          <Tooltip label={<Markdown className='max-w-[30em]'>{description}</Markdown>}>{<div>{c}</div>}</Tooltip>
        )}>
        <div className='font-semibold'>
          {isNullOrEmpty(url) ? title : <ComparisonLink url={url}>{title}</ComparisonLink>}
        </div>
      </WrapIf>
    </div>
    <div className='flex items-center gap-1'>
      <div className='text-lg font-numbers ml-auto font-extrabold' style={{color}}>
        {fmt(value)}
      </div>
      {unitLabel && <div className='text-xs font-semibold opacity-50 font-numbers'>{unitLabel}</div>}
    </div>
  </>
)

type MinimalComparisonForm = Pick<DbFormSubset<ComparisonEdit>, 'getInputProps' | 'values' | 'setFieldValue'>

const ComparisonInputs = ({
  form,
  unitLabel,
  fmt,
  className
}: {
  form: MinimalComparisonForm
  className?: string
  unitLabel?: string
  fmt: (n: number) => string
}) => {
  const {getInputProps: inProps, values, setFieldValue} = form
  return (
    <div className={twMerge('space-y-4', className)}>
      <TextInput className='grow' autoFocus placeholder='Title' variant='title' {...inProps('title')} />
      <div className='flex gap-4'>
        <StringNumberInput
          className='font-semibold'
          placeholder='value'
          {...inProps('value')}
          fmt={fmt}
          rightSection={<div className='pr-1 opacity-50 font-semibold w-full'>{unitLabel}</div>}
          rightSectionWidth='auto'
        />
        <SColorInput
          className='w-[11em]'
          placeholder='custom color'
          popoverProps={{withinPortal: false}}
          {...inProps('color')}
        />
        <ComparisonTypeInput {...inProps('type')} />
      </div>
      <TextInput
        placeholder='URL (optional)'
        {...inProps('url')}
        rightSection={
          values?.url ?
            <div className='flex items-center'>
              <ActionIcon
                component='a'
                href={values.url}
                target='_blank'
                onClick={e => e.stopPropagation()}
                variant='subtle'>
                <HiOutlineExternalLink />
              </ActionIcon>
              <ActionIcon onClick={() => setFieldValue('url', '')} variant='subtle'>
                <HiX />
              </ActionIcon>
            </div>
          : undefined
        }
      />
      <RichText placeholder='description' {...inProps('description')} />
    </div>
  )
}

const ComparisonTypeInput = ({
  value,
  onChange,
  ...props
}: Omit<ComboboxProps, 'data' | 'store'> & {
  value?: ComparisonType
  onChange?: (value: ComparisonType | undefined) => void
}) => {
  const store = useCombobox({})
  const _onChange = (v: ComparisonType | undefined) => {
    store.closeDropdown()
    return onChange?.(v)
  }
  return (
    <Combo
      store={store}
      withinPortal={false}
      onOptionSubmit={v => _onChange(v as ComparisonType | undefined)}
      {...props}>
      <Combo.Target>
        <div className={`flex items-center gap-1 cursor-pointer`} onClick={() => store.openDropdown()}>
          {value ?
            <ComparisonTypeBadge type={value} longLabel />
          : <LabelForm className='text-sm opacity-50 font-semibold'>comparison type</LabelForm>}
          <HiSelector />
        </div>
      </Combo.Target>
      <Combo.Dropdown>
        <Combo.Options>
          {mapEntries(comparisonTypeMd, (t, {desc}) => (
            <Combo.Option key={t} value={t} className='!py-2 flex items-center gap-2'>
              {t == value && <CheckIcon size='1em' color='gray' />}
              <ComparisonTypeBadge type={t} longLabel />
              <div className='text-gray-600'>{desc}</div>
            </Combo.Option>
          ))}
        </Combo.Options>
        {value && (
          <div className='flex justify-end'>
            <Button variant='subtle' size='sm' leftSection={<HiX />} onClick={() => onChange?.(undefined)}>
              clear
            </Button>
          </div>
        )}
      </Combo.Dropdown>
    </Combo>
  )
}
